import { useEffect, useState } from "react";
import { API, api, fmtDate, M_PRIORITY, M_PRIORITY_STYLE, M_STATUS, M_STATUS_STYLE, M_TIPO, M_TIPO_STYLE } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlarmClock, ClipboardList, FileDown, Loader2, Printer, Timer } from "lucide-react";

export default function PendenciasPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    api.get("/tenant/reports/pendencias")
      .then(({ data }) => setData(data))
      .catch(() => { setError(true); setData({ metrics: null, items: [] }); });
  };
  useEffect(() => { load(); }, []);

  if (!data) return <Spinner />;
  const { metrics, items } = data;

  if (error) {
    return (
      <div data-testid="pendencias-page">
        <PageHeader title="Relatório de Pendências" subtitle="Pedidos e ocorrências aguardando tratativa da administração" />
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-8 text-center" data-testid="pendencias-error">
          <p className="text-sm text-rose-400 mb-4">Não foi possível carregar o relatório. Tente novamente.</p>
          <Button data-testid="pendencias-retry" onClick={load}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pendencias-page">
      <PageHeader title="Relatório de Pendências" subtitle="Pedidos e ocorrências aguardando tratativa da administração">
        <Button data-testid="pendencias-print-button" variant="outline" onClick={() => window.print()}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
          <Printer className="h-4 w-4 mr-1.5" /> Imprimir
        </Button>
        <Button data-testid="pendencias-pdf-button" onClick={() => window.open(`${API}/tenant/reports/pendencias.pdf`, "_blank")}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <FileDown className="h-4 w-4 mr-1.5" /> Exportar PDF
        </Button>
      </PageHeader>

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="pendencias-metrics">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-abertos">
            <div className="h-8 w-8 rounded-md bg-rose-400/10 flex items-center justify-center mb-3"><ClipboardList className="h-4 w-4 text-rose-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.abertos}</p>
            <p className="text-xs text-zinc-500 mt-1">Abertos / em análise</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-andamento">
            <div className="h-8 w-8 rounded-md bg-sky-400/10 flex items-center justify-center mb-3"><Loader2 className="h-4 w-4 text-sky-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.em_andamento}</p>
            <p className="text-xs text-zinc-500 mt-1">Em andamento</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-vencidas">
            <div className="h-8 w-8 rounded-md bg-amber-400/10 flex items-center justify-center mb-3"><AlarmClock className="h-4 w-4 text-amber-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.vencidas}</p>
            <p className="text-xs text-zinc-500 mt-1">Vencidas (previsão ultrapassada)</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-tempo">
            <div className="h-8 w-8 rounded-md bg-emerald-400/10 flex items-center justify-center mb-3"><Timer className="h-4 w-4 text-emerald-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.tempo_medio_dias ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Tempo médio de resolução (dias)</p>
          </div>
        </div>
      )}

      {metrics && Object.keys(metrics.por_tipo).length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 mb-6" data-testid="pendencias-por-tipo">
          <p className="text-sm font-medium text-zinc-100 mb-3">Pendências por tipo</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(metrics.por_tipo).map(([tipo, count]) => (
              <span key={tipo} data-testid={`pendencias-tipo-${tipo}`}
                className={`text-xs rounded-full px-2.5 py-1 ${M_TIPO_STYLE[tipo] || "text-zinc-400 bg-zinc-400/10"}`}>
                {M_TIPO[tipo] || tipo}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Nenhuma pendência" subtitle="Todos os pedidos e ocorrências foram concluídos ou cancelados." />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Nº</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Tipo</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Assunto</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Solicitante</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Unidade</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Status</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Prioridade</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Abertura</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Previsão</th>
                <th className="px-3 py-3 text-xs font-medium text-zinc-400">Responsável</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const vencida = m.data_previsao && new Date(m.data_previsao) < new Date();
                return (
                  <tr key={m.id} data-testid={`pendencias-row-${m.id}`}
                    className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-200">
                    <td className="px-3 py-3 font-mono text-xs text-zinc-400">#{String(m.number).padStart(4, "0")}</td>
                    <td className="px-3 py-3"><Badge className={`${M_TIPO_STYLE[m.tipo]} border-0 text-[11px] font-medium`}>{M_TIPO[m.tipo]}</Badge></td>
                    <td className="px-3 py-3 text-zinc-100 max-w-56 truncate" title={m.assunto}>{m.assunto}</td>
                    <td className="px-3 py-3 text-zinc-300 text-xs max-w-36 truncate">{m.is_anonimo ? "Anônimo" : m.solicitante_nome}</td>
                    <td className="px-3 py-3 text-zinc-400 text-xs max-w-28 truncate">{m.eql_label || "—"}</td>
                    <td className="px-3 py-3"><Badge className={`${M_STATUS_STYLE[m.status]} border-0 text-[11px] font-medium`}>{M_STATUS[m.status]}</Badge></td>
                    <td className="px-3 py-3"><Badge className={`${M_PRIORITY_STYLE[m.prioridade]} border-0 text-[11px] font-medium`}>{M_PRIORITY[m.prioridade]}</Badge></td>
                    <td className="px-3 py-3 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(m.created_at)}</td>
                    <td className={`px-3 py-3 text-xs whitespace-nowrap ${vencida ? "text-rose-400 font-medium" : "text-zinc-400"}`}>{fmtDate(m.data_previsao)}</td>
                    <td className="px-3 py-3 text-zinc-400 text-xs max-w-32 truncate">{m.responsavel_name || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
